import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowLeft, SpinnerGap, Wallet, Briefcase, CurrencyInr, UsersThree } from '@phosphor-icons/react';
import { fmtINR, fmtUSD } from '../utils/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  approved: 'bg-emerald-100 text-emerald-700',
  sa_approved: 'bg-emerald-100 text-emerald-700',
  manager_approved: 'bg-teal-100 text-teal-700',
  lead_approved: 'bg-cyan-100 text-cyan-700',
  submitted: 'bg-blue-100 text-blue-700',
  reviewing: 'bg-amber-100 text-amber-700',
  pending_lead: 'bg-amber-100 text-amber-700',
  pending_lead_review: 'bg-amber-100 text-amber-700',
  pending_manager: 'bg-orange-100 text-orange-700',
  pending_manager_review: 'bg-orange-100 text-orange-700',
};

const RoleIncentivePage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isLead = ['sales_lead', 'recruiting_lead'].includes(user?.role);
  const fallbackTitle = isLead ? 'Lead Incentive' : 'Manager Incentive';

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/role-incentives`, { withCredentials: true });
      setData(res.data);
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Failed to load role incentives');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <SpinnerGap size={32} className="animate-spin text-blue-600" />
      </div>
    );
  }

  const pageTitle = data?.page_title || fallbackTitle;
  const placements = data?.placements || [];
  const total = data?.total_share || 0;
  const roleWord = isLead ? 'lead' : 'manager';

  return (
    <div className="space-y-6" data-testid="role-incentive-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9" data-testid="back-btn">
          <ArrowLeft size={20} />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Outfit']" data-testid="role-incentive-title">
            {pageTitle}
          </h1>
          <p className="text-slate-500 mt-1 text-sm">
            Your {roleWord} share from team placements. Recruiter incentive amounts are never shown.
          </p>
        </div>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-sm text-red-700">{error}</CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-slate-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase text-slate-500 font-semibold">Total {isLead ? 'Lead' : 'Manager'} Share</p>
                <p className="text-3xl font-bold text-emerald-700 mt-1" data-testid="role-incentive-total">
                  {fmtINR(total)}
                </p>
              </div>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                <CurrencyInr size={24} className="text-emerald-600" weight="bold" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <p className="text-xs uppercase text-slate-500 font-semibold">Team Placements</p>
            <p className="text-3xl font-bold text-blue-700 mt-1" data-testid="role-incentive-count">
              {placements.length}
            </p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <p className="text-xs uppercase text-slate-500 font-semibold">Avg Share / Placement</p>
            <p className="text-3xl font-bold text-slate-700 mt-1">
              {placements.length > 0 ? fmtINR(Math.round(total / placements.length)) : fmtINR(0)}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200" data-testid="role-incentive-table-card">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-['Outfit'] flex items-center gap-2">
            <UsersThree size={18} className="text-blue-600" />
            {isLead ? 'Lead' : 'Manager'} Share by Team Placement
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {placements.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              <Wallet size={48} className="mx-auto mb-3 opacity-50" />
              <p className="font-medium">No {roleWord} shares yet</p>
              <p className="text-sm mt-1">
                When your team members have placements, your {roleWord} share will appear here.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] text-slate-400 uppercase tracking-wider bg-slate-50 border-b border-slate-100">
                    <th className="px-4 py-2.5 text-left font-semibold">Candidate</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Module</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Category</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Created By</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Bill Rate</th>
                    <th className="px-3 py-2.5 text-right font-semibold">Margin</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Status</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Your Role</th>
                    <th className="px-3 py-2.5 text-left font-semibold">Date</th>
                    <th className="px-4 py-2.5 text-right font-semibold">Your Share</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {placements.map((p) => (
                    <tr
                      key={p.placement_id}
                      className="hover:bg-slate-50/60 transition-colors"
                      data-testid={`role-incentive-row-${p.placement_id}`}
                    >
                      <td className="px-4 py-3">
                        <button
                          onClick={() => navigate(`/dashboard/placements/${p.placement_id}`)}
                          className="font-medium text-blue-700 hover:text-blue-900 underline text-left"
                        >
                          {p.candidate_name}
                        </button>
                        {(p.vendor || p.client) && (
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            {[p.vendor, p.client].filter(Boolean).join(' / ')}
                          </p>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <Badge variant="outline" className="text-[10px]">
                          {p.module === 'recruiting' ? 'Recruiting' : 'Sales'}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600">
                        {(p.category || '-').replace(/_/g, ' ')}
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-600 font-medium">
                        {p.created_by_name || '-'}
                      </td>
                      <td className="px-3 py-3 text-right text-xs">{fmtUSD(p.bill_rate)}/hr</td>
                      <td className="px-3 py-3 text-right text-xs font-medium">{fmtUSD(p.margin)}/hr</td>
                      <td className="px-3 py-3">
                        <Badge className={`text-[10px] ${STATUS_STYLES[p.status] || 'bg-slate-100 text-slate-600'}`}>
                          {(p.status || '-').replace(/_/g, ' ')}
                        </Badge>
                      </td>
                      <td className="px-3 py-3">
                        <Badge className="text-[9px] bg-emerald-50 text-emerald-700 border-emerald-200">
                          {p.role_label}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-xs text-slate-500 whitespace-nowrap">
                        {(p.start_date || p.approved_at || '').slice(0, 10) || '-'}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-700" data-testid={`role-share-${p.placement_id}`}>
                        {fmtINR(p.your_share)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 border-t-2 border-slate-200">
                    <td colSpan="9" className="px-4 py-3 text-right font-semibold text-slate-600 text-sm">
                      Total {isLead ? 'Lead' : 'Manager'} Share
                    </td>
                    <td className="px-4 py-3 text-right font-bold text-lg text-emerald-700">
                      {fmtINR(total)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-slate-400 flex items-center gap-1.5">
        <Briefcase size={14} />
        Only your {roleWord} incentive share is listed. Team member (recruiter) payout amounts are hidden.
      </p>
    </div>
  );
};

export default RoleIncentivePage;
